import React, {FunctionComponent, useCallback} from 'react'
import {
  CalendarIcon, ChartBarIcon,
  EyeIcon, FilterIcon,
  HandIcon,
  KeyIcon, PlusCircleIcon,
  TemplateIcon, UsersIcon
} from "@heroicons/react/outline";
import {TooltipCategory, TooltipContent} from "../../../themes/SharedStyledComponent";
import {getStarsDisplay, useGetPopoverRowWithIcon} from "./DatamodelRights.utils";
import {ExpandedWorkspace} from "../../../redux/models/workspace";
import styled from "styled-components";
import Language from "../../../language";

enum RowParamsTypes {
  "divider" = "divider",
  "row" = "row"
}

interface RowParams {
  type: RowParamsTypes.divider | RowParamsTypes.row
}

interface IconRowParams extends RowParams {
  type: RowParamsTypes.row
  key: string
  isEnabled: boolean
  Icon: FunctionComponent
  starCount?: number
}

interface DividerRowParams extends RowParams {
  type: RowParamsTypes.divider
}

enum Definition {
  onlyCompatibleCharts = "onlyCompatibleCharts",
  onlyDatamodelDimension = "onlyDatamodelDimension"
}

const getWorkspaceRightsGlobalPopover = (
  getPopoverRowWithIcon: (isEnabled: boolean, languageArgs: {key: string, args?: any}, Icon: React.FunctionComponent, starCount?: number) => JSX.Element,
  workspace: Pick<ExpandedWorkspace, "name">,
  hasWorkspaceReaderAccess: boolean,
  hasWorkspaceWriterAccess: boolean,
  hasWorkspaceManagerAccess: boolean,
  isDatamodelExplorer: boolean,) => {

  const definitions = [
    ...(hasWorkspaceReaderAccess ? [{
      type: Definition.onlyCompatibleCharts,
      label: Language.get("admin.workspaces.popup.definition.onlyCompatibleCharts")
    }] : []),
    ...((hasWorkspaceReaderAccess || hasWorkspaceWriterAccess || hasWorkspaceManagerAccess) ? [{
      type: Definition.onlyDatamodelDimension,
      label: Language.get("admin.workspaces.popup.definition.onlyDatamodelDimension")
    }] : []),
  ].map((definition, index) => ({...definition, starCount: index + 1}))

  const params: (IconRowParams | DividerRowParams)[] = [
    {
      type: RowParamsTypes.row,
      key: "workspaceAccess",
      isEnabled: hasWorkspaceReaderAccess,
      Icon: TemplateIcon
    },
    ...(hasWorkspaceReaderAccess ? [{
      type: RowParamsTypes.row,
      key: "readChartAccess",
      isEnabled: true,
      Icon: EyeIcon
    },
      {
        type: RowParamsTypes.row,
        key: "periodModificationAccess",
        starCount: definitions.find(definition => definition.type === Definition.onlyCompatibleCharts)?.starCount,
        isEnabled: true,
        Icon: CalendarIcon
      },
      {
        type: RowParamsTypes.row,
        starCount: definitions.find(definition => definition.type === Definition.onlyDatamodelDimension)?.starCount,
        key: "applyFilterAccess",
        isEnabled: true,
        Icon: FilterIcon
      }] : []),
    ...(hasWorkspaceWriterAccess ? [
      {
        type: RowParamsTypes.divider
      }, {
        type: RowParamsTypes.row,
        key: "createChartAccess",
        isEnabled: true,
        Icon: PlusCircleIcon
      },
      {
        type: RowParamsTypes.row,
        key: "updateChartAccess",
        isEnabled: true,
        Icon: ChartBarIcon
      },
      {
        type: RowParamsTypes.row,
        key: "editDashboardAccess",
        isEnabled: true,
        Icon: HandIcon
      },
      ...(isDatamodelExplorer ? [{
        type: RowParamsTypes.row,
        key: "personalDashboardAccess",
        isEnabled: true,
        Icon: KeyIcon
      }] : [])] : []),
    ...(hasWorkspaceManagerAccess ? [{
      type: RowParamsTypes.divider
    }, {
      type: RowParamsTypes.row,
      key: "manageWorkspaceAccess",
      isEnabled: true,
      Icon: UsersIcon
    }] : [])
  ] as (IconRowParams | DividerRowParams)[]
  return <>
    <TooltipCategory>{Language.get('admin.workspaces.popup.titlePrefix')} {workspace.name}</TooltipCategory>
    {params.map((element: IconRowParams | DividerRowParams) => element.type === RowParamsTypes.divider
      ? <Divider key={element.type}></Divider> : getPopoverRowWithIcon(
        element.isEnabled,
        {key: `admin.workspaces.popup.${element.key}`},
        element.Icon,
        element.starCount,
      ))}
    {definitions.length > 0 && <br/>}
    {definitions.map(definition => <DefinitionLine key={definition.label}>
        {getStarsDisplay(definition.starCount)}
        {definition.label}
      </DefinitionLine>
    )}
  </>
}

const Divider = styled.div`
    border-top: 1px solid var(--light-grey-2);
`

const DefinitionLine = styled.div`
    display: flex;
    gap: 5px;
    font-size: 10px;
`

export const useGetWorkspaceRightsGlobalPopover = () => {
  const getPopoverRowWithIcon = useGetPopoverRowWithIcon()
  return useCallback((
    workspace: Pick<ExpandedWorkspace, "name">,
    hasWorkspaceReaderAccess: boolean,
    hasWorkspaceWriterAccess: boolean,
    hasWorkspaceManagerAccess: boolean,
    isDatamodelExplorer: boolean,
  ) => {
    return getWorkspaceRightsGlobalPopover(getPopoverRowWithIcon, workspace, hasWorkspaceReaderAccess, hasWorkspaceWriterAccess, hasWorkspaceManagerAccess, isDatamodelExplorer)
  }, [getPopoverRowWithIcon])
}
